/**
 * V2 line-item semantics — mirrors the classic maps EXACTLY and maps
 * them onto V2 token CSS variables.
 *
 * Keys are kept byte-identical to classic so behaviour is preserved:
 *   - ACTIVITY_SYMBOLS  <- ScopeLineItem.tsx (RULE-008)
 *   - SOURCE_META       <- shell.jsx SOURCE_META (source -> color band/legend)
 *   - CONF_META         <- shell.jsx CONF_META (confidence pill)
 *   - PROV_META         <- provenance pill (supplement)
 *   - LOSS_KIND         <- shell.jsx LOSS_KIND (loss-type pill)
 *
 * `color` values are V2 token references (e.g. `var(--info)`), not the
 * classic Tailwind color classes.
 */

/** Semantic pill kind shared across V2 pills/badges. */
export type SemanticKind = 'ok' | 'warn' | 'bad' | 'info' | 'neutral' | 'accent';

// ================================================================= //
//  Activity symbols (copied EXACTLY from ScopeLineItem.tsx:39-48)     //
// ================================================================= //

/**
 * Maps activity strings to single-character action symbols (RULE-008).
 * Lookup is case-insensitive; unknown values fall back to the raw string.
 */
export const ACTIVITY_SYMBOLS: Record<string, string> = {
  replace: '+',
  remove: '-',
  'remove and replace': '&',
  'remove & replace': '&',
  'detach and reset': 'R',
  'detach & reset': 'R',
  'material only': 'M',
  'install only': 'I',
};

/** Symbol -> human tooltip label (shell.jsx ACT_LABEL). */
export const ACT_LABEL: Record<string, string> = {
  '+': 'Replace',
  '-': 'Remove',
  '&': 'Remove & replace',
  R: 'Detach & reset',
  M: 'Material only',
  I: 'Install only',
  '—': 'Unmapped',
};

/**
 * Resolves an activity string to its display symbol (case-insensitive).
 *
 * @param activity - The raw activity string.
 * @returns The mapped symbol, or the original string when unrecognised.
 */
export function activitySymbol(activity: string): string {
  const key = (activity || '').trim().toLowerCase();
  return ACTIVITY_SYMBOLS[key] ?? (activity || '—');
}

// ================================================================= //
//  Source meta (shell.jsx SOURCE_META -> V2 tokens)                   //
// ================================================================= //

/** Display meta for a line-item source: legend label + V2 token color. */
export interface SourceMetaEntry {
  label: string;
  color: string;
}

/**
 * Source -> { label, color }. Keys mirror classic exactly:
 * pattern->info, rules_engine/code_requirement->violet,
 * ai_suggestion->warn, companion->teal, user_input->neutral.
 */
export const SOURCE_META: Record<string, SourceMetaEntry> = {
  pattern: { label: 'Pattern', color: 'var(--info)' },
  rules_engine: { label: 'Rules engine', color: 'var(--violet)' },
  code_requirement: { label: 'Code requirement', color: 'var(--violet)' },
  ai_suggestion: { label: 'AI suggestion', color: 'var(--warn)' },
  companion: { label: 'Companion', color: 'var(--teal)' },
  user_input: { label: 'User input', color: 'var(--bd-strong)' },
};

/** Fallback for an unknown source. */
export const SOURCE_META_FALLBACK: SourceMetaEntry = {
  label: 'Unknown',
  color: 'var(--tx-faint)',
};

/**
 * Looks up source meta with a graceful fallback.
 *
 * @param source - The line-item source key.
 * @returns The matching SourceMetaEntry, or the neutral fallback.
 */
export function sourceMeta(source: string | undefined | null): SourceMetaEntry {
  return SOURCE_META[(source || '').trim()] ?? SOURCE_META_FALLBACK;
}

// ================================================================= //
//  Confidence meta (shell.jsx CONF_META)                              //
// ================================================================= //

/** Confidence pill meta: V2 pill kind + label. */
export interface ConfMetaEntry {
  kind: SemanticKind;
  label: string;
}

/** high->ok, medium->warn, low->bad. */
export const CONF_META: Record<string, ConfMetaEntry> = {
  high: { kind: 'ok', label: 'High' },
  medium: { kind: 'warn', label: 'Medium' },
  low: { kind: 'bad', label: 'Low' },
};

// ================================================================= //
//  Provenance meta (supplement provenance pill)                       //
// ================================================================= //

/** Provenance pill meta: V2 pill kind + label. */
export interface ProvMetaEntry {
  kind: SemanticKind;
  label: string;
}

/**
 * Provenance -> pill meta.
 * ORIGINAL/UNCHANGED->neutral, ADDED->ok, CHANGED->warn,
 * UNMAPPED->bad, MANUAL->info, ENHANCED->accent.
 *
 * ENHANCED (B-531 slice 3) is a faithful line whose code was swapped by an
 * approved MODIFY. `accent` reads as "notable but ok" — the same purple-ish
 * treatment used for accent pills — distinct from ADDED (ok/green) and CHANGED
 * (warn), because the line is neither net-new nor a correction, it was upgraded.
 */
export const PROV_META: Record<string, ProvMetaEntry> = {
  ORIGINAL: { kind: 'neutral', label: 'Original' },
  UNCHANGED: { kind: 'neutral', label: 'Unchanged' },
  ADDED: { kind: 'ok', label: 'Added' },
  CHANGED: { kind: 'warn', label: 'Changed' },
  UNMAPPED: { kind: 'bad', label: 'Unmapped' },
  MANUAL: { kind: 'info', label: 'Manual' },
  ENHANCED: { kind: 'accent', label: 'Enhanced' },
};

// ================================================================= //
//  Loss kind (shell.jsx LOSS_KIND -> V2 pill kind)                    //
// ================================================================= //

/** Loss type -> pill kind (shell.jsx LOSS_KIND). */
export const LOSS_KIND: Record<string, SemanticKind> = {
  Hurricane: 'info',
  Wind: 'info',
  Water: 'info',
  Hail: 'neutral',
  Fire: 'bad',
  Flood: 'info',
  Mold: 'warn',
};
